import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

function AnimatedNumber({ value, duration = 1.2 }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (value == null) return;
    let start = 0;
    const target = value || 0;
    const steps = Math.max(1, Math.ceil(duration * 50));
    const inc = target / steps;
    const timer = setInterval(() => {
      start += inc;
      if (start >= target) {
        setN(target);
        clearInterval(timer);
      } else {
        setN(Math.floor(start));
      }
    }, 1000 / 50);
    return () => clearInterval(timer);
  }, [value, duration]);
  return <span className="tabular-nums">{n}</span>;
}

/**
 * PipelineBar — horizontal stacked bar showing a pipeline distribution.
 * segments: [{ label, value, color, accent? }]
 */
export function PipelineBar({ total, segments, delay = 0 }) {
  const safe = total || 0;
  return (
    <div className="space-y-3">
      {segments.map((seg, i) => {
        const pct = safe === 0 ? 0 : Math.round((seg.value / safe) * 100);
        return (
          <div key={seg.label}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-mono text-[#888] tracking-[0.15em] uppercase">
                {seg.label}
              </span>
              <span className="text-[11px] font-mono text-[#ccc] tabular-nums">
                <AnimatedNumber value={seg.value} /> <span className="text-[#444]">·</span>{' '}
                <span className="text-[#666]">{pct}%</span>
              </span>
            </div>
            <div className="h-1.5 bg-[#121212] overflow-hidden relative">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 1.1, delay: delay + i * 0.08, ease: 'easeOut' }}
                className="h-full"
                style={{
                  background: seg.color,
                  boxShadow: seg.accent ? `0 0 10px ${seg.color}88` : 'none',
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * PipelineFlow — connected review stages New → Under Review → Shortlisted → Accepted
 */
export function PipelineFlow({ stages }) {
  // stages: [{ key, label, value, color }]
  const maxVal = Math.max(1, ...stages.map((s) => s.value || 0));
  return (
    <div className="relative">
      <div className="flex items-stretch gap-0">
        {stages.map((s, i) => {
          const height = 10 + ((s.value || 0) / maxVal) * 48;
          return (
            <div key={s.key} className="flex-1 flex flex-col items-center">
              {/* Bar */}
              <div className="w-full flex items-end justify-center h-16">
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height, opacity: 1 }}
                  transition={{ duration: 0.9, delay: i * 0.12, ease: 'easeOut' }}
                  style={{
                    width: '80%',
                    background: s.color,
                    boxShadow: `0 0 12px ${s.color}55`,
                  }}
                />
              </div>
              {/* Connector line */}
              {i < stages.length - 1 && (
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.6, delay: 0.3 + i * 0.12 }}
                  className="absolute h-px bg-[#222]"
                  style={{
                    width: `${100 / stages.length}%`,
                    top: 30,
                    left: `${((i + 0.5) / stages.length) * 100}%`,
                    transformOrigin: 'left',
                  }}
                />
              )}
              <div className="mt-3 text-center">
                <p className="font-outfit text-lg font-semibold text-white">
                  <AnimatedNumber value={s.value || 0} />
                </p>
                <p className="text-[9px] font-mono text-[#666] tracking-[0.15em] uppercase mt-0.5">
                  {s.label}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { AnimatedNumber };
